import { CoreApplicationContext } from '@eventbrite/context-gen';
import { getNarrowMenuInfo } from '@eventbrite/eds-global-header';
import { Icon } from '@eventbrite/eds-icon';
import { IconButton } from '@eventbrite/eds-icon-button';
import {
    MagnifyingGlassChunky as MagnifyingGlassChunkySvg,
    PlusChunky as PlusChunkySvg,
} from '@eventbrite/eds-iconography';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import {
    FALLBACK_SUGGESTIONS,
    getCreateEventCTA,
    getHelpMenu,
    getLogoInfo,
    getNarrowMenuLinks,
    getNonUserQuickLinks,
    getOrganizeMenu,
    getUserMenuInfo,
} from '@eventbrite/site-header';
import loadable from '@loadable/component';
import classNames from 'classnames';
import React from 'react';
import { FeatureFlags, RequestShape, UserShape } from '../../types/index';
import './ConsumerHeaderIndex.scss';
import {
    getBugQuickLink,
    getLikesQuickLinks,
    getMyTicketsQuickLinks,
} from './core-js-global-copies';
import GlobalHeaderLogo from './GlobalHeaderLogo';
import {
    GlobalHeaderCallToAction,
    GlobalHeaderMenu,
    GlobalHeaderMenus,
    GlobalHeaderQuickLinks,
} from './GlobalHeaderNavigationBar';
import GlobalHeaderSearch, { getSearchInfo } from './GlobalHeaderSearch';

const LazySearchTakeover = loadable.lib(
    () => import('@eventbrite/search-takeover'),
);

interface ConsumerHeaderProps {
    env: CoreApplicationContext['env'] & {
        launchNewSignInSignUpLinks?: string;
        isManageOverviewActive?: boolean;
    };
    request: RequestShape;
    user: UserShape;
    onSearchSelect: Function;
    onAdditionalMenuTextItemSelect?: Function;
    handleSearch: Function;
    onSearchFocus?: Function;
    isHomePage?: boolean;
    isSearchPage?: boolean;
    isBrowsePage?: boolean;
    isMobile?: boolean;
    id?: string;
    featureFlags: FeatureFlags;
}

class ConsumerHeader extends React.PureComponent<ConsumerHeaderProps> {
    static defaultProps = {
        id: 'global-header',
    };

    constructor(props: ConsumerHeaderProps) {
        super(props);

        this.state = {
            searchQuery: props.env.searchQuery || '',
            searchSuggestions: FALLBACK_SUGGESTIONS,
            isSearchTakeoverVisible: null,
        };
    }

    _handleSearchClick = () => {
        this.setState({ isSearchTakeoverVisible: true });
    };

    _handleOnAdditionalMenuTextItemSelect = (value: string, index: number) => {
        if (this.props.onAdditionalMenuTextItemSelect) {
            this.props.onAdditionalMenuTextItemSelect(value, index);
        }
    };

    _showCreateEventCTA = (
        { canCreateEvents }: UserShape,
        requestPath: string,
    ) => {
        // Only show the Create Event Cta if the user can create events, or is on the homepage
        const isHomePage = requestPath === '/';
        return canCreateEvents || isHomePage;
    };

    _getHeaderLinks = () => {
        const {
            env: {
                ebDomain,
                helpUrls,
                signinUrl,
                signupUrl,
                loginUrl,
                organizeUrls,
                serverUrl,
                launchNewSignInSignUpLinks,
            },
            request: { path: requestPath },
            user,
            featureFlags,
        } = this.props;

        const { isAuthenticated, publicId } = user;

        const likesLinks = getLikesQuickLinks(
            serverUrl,
            ebDomain,
            requestPath || '',
            publicId,
        );
        const myTicketsLinks = getMyTicketsQuickLinks(
            serverUrl,
            ebDomain,
            requestPath || '',
            publicId,
        );

        const showCreateEventCTA = this._showCreateEventCTA(
            user,
            requestPath || '',
        );
        const callToActionInfo = getCreateEventCTA(
            serverUrl,
            ebDomain,
            requestPath,
            isAuthenticated,
            { showCreateEventCTA },
        );

        const additionalMenus = [
            getOrganizeMenu(organizeUrls, requestPath),
            getHelpMenu(helpUrls, requestPath),
        ];
        const ctaIcon = <Icon size="small" type={<PlusChunkySvg />} />;

        let linkComponents: any[] = [];

        // on the search page, show britelings a link to report a bug
        if (featureFlags.showReportBugLinkInHeader) {
            linkComponents = [
                <GlobalHeaderQuickLinks
                    key="bugLinks"
                    links={getBugQuickLink()}
                    shouldShowIcon={true}
                />,
            ];
        }

        linkComponents = [
            ...linkComponents,

            <GlobalHeaderCallToAction
                key="createEventLink"
                linkInfo={callToActionInfo}
                iconType={ctaIcon}
                shouldShowIcon={true}
                iconColor="grey-800"
            />,
            <GlobalHeaderQuickLinks
                key="likesLinks"
                links={likesLinks}
                shouldShowIcon={true}
            />,
            <GlobalHeaderQuickLinks
                key="myTicketsLinks"
                links={myTicketsLinks}
                shouldShowIcon={true}
            />,
        ];

        if (!isAuthenticated) {
            const signinPath = launchNewSignInSignUpLinks
                ? signinUrl
                : loginUrl;
            const nonUserQuickLinks = getNonUserQuickLinks(
                requestPath,
                isAuthenticated,
                signinPath,
                signupUrl,
                { launchNewSignInSignUpLinks },
            );

            linkComponents = [
                <GlobalHeaderMenus
                    key="additionalMenus"
                    additionalMenus={additionalMenus}
                    onAdditionalMenuTextItemSelect={
                        this._handleOnAdditionalMenuTextItemSelect
                    }
                />,
                <GlobalHeaderCallToAction
                    key="createEventLinkNotAuth"
                    linkInfo={callToActionInfo}
                    shouldShowIcon={false}
                />,
                <GlobalHeaderQuickLinks
                    key="nonUserQuickLinks"
                    links={nonUserQuickLinks}
                    shouldShowIcon={false}
                />,
            ];
        }

        return <div className="consumer-header__links">{linkComponents}</div>;
    };

    _getGlobalHeaderSearch = (searchInfo: {
        label: string | GenericLazyString;
    }) => {
        const { id, isSearchPage, isHomePage, isBrowsePage } = this.props;

        if (!isSearchPage) {
            return (
                <GlobalHeaderSearch
                    id={`${id}-desktop-search`}
                    searchInfo={searchInfo}
                    isHomePage={isHomePage || false}
                    isBrowsePage={isBrowsePage || false}
                    setRenderSearchTakeover={(val: boolean) =>
                        this.setState({ isSearchTakeoverVisible: val })
                    }
                />
            );
        }

        return <div className="consumer-header__search__placeholder" />;
    };

    _getMobileHeaderLinks = () => {
        const {
            user,
            env: {
                adminServerUrl,
                ebDomain,
                loginUrl,
                logoutUrl,
                signinUrl,
                signupUrl,
                helpUrls,
                organizeUrls,
                isManageOverviewActive,
                serverUrl,
                launchNewSignInSignUpLinks,
            },
            request: { path: requestPath },
            featureFlags,
        } = this.props;

        const { isAuthenticated } = user;
        const additionalMenus = [
            getOrganizeMenu(organizeUrls, requestPath),
            getHelpMenu(helpUrls, requestPath),
        ];
        const userMenuInfo = getUserMenuInfo({
            adminServerUrl,
            featureFlags,
            isManageOverviewActive,
            logoutUrl,
            requestPath,
            serverUrl,
            showBrowseEvents: true,
            user,
        });

        const signinPath = launchNewSignInSignUpLinks ? signinUrl : loginUrl;
        const nonUserQuickLinks = getNonUserQuickLinks(
            requestPath,
            isAuthenticated,
            signinPath,
            signupUrl,
            { launchNewSignInSignUpLinks },
        );

        const showCreateEventCTA = this._showCreateEventCTA(
            user,
            requestPath || '',
        );
        const callToActionInfo = getCreateEventCTA(
            serverUrl,
            ebDomain,
            requestPath,
            isAuthenticated,
            { showCreateEventCTA },
        );

        const narrowMenuInfo = !isAuthenticated
            ? getNarrowMenuLinks({
                  serverUrl,
                  requestPath,
                  createEventCTA: callToActionInfo,
              })
            : getNarrowMenuInfo(
                  userMenuInfo,
                  additionalMenus,
                  [],
                  callToActionInfo,
                  [],
                  true,
              );

        let narrowMenu;

        if (narrowMenuInfo) {
            const className = classNames(
                'eds-global-header__narrow-menu',
                'eds-show-down-mn',
            );

            narrowMenu = [narrowMenuInfo].map((menuInfo) => (
                <span key={menuInfo.label} className={className}>
                    <GlobalHeaderMenu
                        menuInfo={menuInfo}
                        data-spec="global-header-narrow-menu"
                        noLabel={true}
                    />
                </span>
            ));
        }

        return (
            <div className="consumer-header__links">
                {narrowMenu}
                <GlobalHeaderQuickLinks
                    links={nonUserQuickLinks}
                    shouldShowIcon={false}
                />
            </div>
        );
    };

    render() {
        const {
            id,
            env,
            user,
            request,
            isHomePage,
            isSearchPage,
            featureFlags,
        } = this.props;
        const {
            searchQuery,
            searchSuggestions,
            isSearchTakeoverVisible,
        }: any = this.state;
        const { serverUrl, adminServerUrl, logoutUrl } = env;
        const { path: requestPath } = request;
        const logoInfo = getLogoInfo(serverUrl, requestPath, {
            iconWidth: '110px',
        });
        const searchInfo = getSearchInfo(
            serverUrl,
            searchQuery,
            searchSuggestions,
        );
        const userMenuInfo = getUserMenuInfo({
            adminServerUrl,
            featureFlags,
            logoutUrl,
            requestPath,
            serverUrl,
            showBrowseEvents: true,
            user,
        });

        return (
            <header
                id={id}
                className={`consumer-header ${
                    isHomePage ? '' : 'consumer-header__not-homepage'
                }`}
                data-spec="consumer-header"
            >
                <a
                    className="consumer-header__skip-links eds-is-hidden-accessible"
                    href="#skip-heading"
                >
                    {gettext('Skip Main Navigation')}
                </a>
                <div className="consumer-header__main" data-role="header">
                    {/* Switching to eds-show-up/down for now to fix the header CSS issues on mobile */}
                    {/* <MediaQuery breakpoint="isMediumUp" forceInitialRender={!isMobile}> */}
                    <div
                        className="consumer-header__content consumer-header__desktop eds-show-up-md"
                        data-spec="consumer-header-desktop"
                    >
                        <GlobalHeaderLogo info={logoInfo} />
                        {this._getGlobalHeaderSearch(searchInfo)}
                        {this._getHeaderLinks()}
                        <GlobalHeaderMenus userMenuInfo={userMenuInfo} />
                    </div>
                    {/* </MediaQuery> */}

                    {/* Switching to eds-show-up/down for now to fix the header CSS issues on mobile */}
                    {/* <MediaQuery breakpoint="isMediumDown" forceInitialRender={isMobile}> */}
                    <div
                        className="consumer-header__content consumer-header__mobile eds-show-down-mn eds-g-cell eds-g-cell--has-overflow eds-g-cell-1-1"
                        data-spec="consumer-header-mobile"
                    >
                        <div className="consumer-header__mobile-links eds-align--space-between">
                            <GlobalHeaderLogo info={logoInfo} />
                            <div className="eds-align--center-vertical">
                                {!isSearchPage && (
                                    <div className="consumer-header__mobile-search-button">
                                        <IconButton
                                            iconType={
                                                <MagnifyingGlassChunkySvg />
                                            }
                                            title={gettext('Search button')}
                                            iconColor="grey-600"
                                            onClick={this._handleSearchClick}
                                        />
                                    </div>
                                )}
                                {this._getMobileHeaderLinks()}
                            </div>
                        </div>
                    </div>
                </div>

                <span id="skip-heading" className="eds-is-hidden-accessible">
                    {gettext('Page Content')}
                </span>
                {isSearchTakeoverVisible !== null ? (
                    <LazySearchTakeover>
                        {({ SearchTakeover }: { SearchTakeover: any }) => (
                            <SearchTakeover
                                domNodeId="root"
                                onClose={() =>
                                    this.setState({
                                        isSearchTakeoverVisible: false,
                                    })
                                }
                                id="search-takeover"
                                isVisible={isSearchTakeoverVisible}
                            />
                        )}
                    </LazySearchTakeover>
                ) : null}
            </header>
        );
    }
}

export default ConsumerHeader;
